import React, { useCallback, useState, useEffect } from "react"
import PropTypes from "prop-types"
import ZoomButtons from "components/map/ZoomButtons"
import Spinner from "components/common/Spinner"
import Icon from "components/common/Icon"
import { Text, Title } from "ui/Typography"

import { Circle, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api"
import { useTranslation } from "react-i18next"

import { API_OPTIONS, DEFAULT_ZOOM, MAP_OPTIONS } from "constants/map"
import { getGuideLocation } from "store/guide-profile"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import styles from "./index.module.css"
import { MapPin } from "ui/Icon/DSIcon/MapPin"

const GuideLocation = ({ height = 500 }) => {
  const { t } = useTranslation()
  const { isLoaded, loadError } = useJsApiLoader(API_OPTIONS)

  const [map, setMap] = useState(null)
  const zoom = map?.zoom || DEFAULT_ZOOM
  const { slug } = useParams()
  const dispatch = useDispatch()
  const locationData = useSelector((store) => store?.guideProfile?.location?.data)
  const { adventure_mode } = useSelector((store) => store.user)
  const themeColor = adventure_mode == "sportsman" ? "#1d72f4" : "#d92c9e"

  const onLoad = useCallback((mapInstance) => {
    setMap(mapInstance)
  }, [])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  const rawLat = locationData?.location?.latitude
  const rawLng = locationData?.location?.longitude

  const latLng = rawLat && rawLng ? { lat: Number(rawLat), lng: Number(rawLng) } : null

  const radiusInMeters = locationData?.location?.radius ? locationData.location.radius * 1609.34 : 0

  useEffect(() => {
    if (slug) {
      dispatch(getGuideLocation(slug))
    }
  }, [slug, dispatch])

  if (loadError) {
    return <div>{t("guide.navigation.error_loading_maps")}</div>
  }

  if (!isLoaded) {
    return <Spinner />
  }

  return (
    <>
      <div className="mb-xs">
        <Title level={3}>{t("guide.navigation.service_area")}</Title>
      </div>
      <GoogleMap
        mapContainerClassName="rounded"
        mapContainerStyle={{ height }}
        options={{ ...MAP_OPTIONS, draggable: false }}
        center={latLng}
        zoom={zoom}
        onLoad={onLoad}
        onUnmount={onUnmount}
      >
        <Circle
          center={latLng}
          radius={radiusInMeters}
          options={{
            strokeColor: themeColor,
            strokeOpacity: 0.8,
            strokeWeight: 2,
            fillColor: themeColor,
            fillOpacity: 0.12,
            clickable: false
          }}
        />

        <Marker
          position={latLng}
          icon={
            ((<MapPin fill="var(--color-primary)" />),
            {
              // ...MARKER_ICON,

              scaledSize: new window.google.maps.Size(30, 30)
            })
          }
        />
        <div className={styles.addressDropdownWrapper}>
          <div className={styles.addressDropdown}>
            <Icon
              iconName="Location"
              className={styles.locationIcon}
              size={22}
              style={{ color: "var(--color-primary)", marginRight: 8, verticalAlign: "middle" }}
            />
            {locationData?.location?.address}
          </div>
          <div className={styles.mapControls}>
            <ZoomButtons />
          </div>
        </div>
      </GoogleMap>
      <div className="mt-l">
        <Title level={5}>{t("guide.navigation.meeting_instructions")}</Title>
        <div style={{ marginTop: 8 }}>
          <Text>{locationData?.location?.meetinginstructions}</Text>
        </div>
      </div>
    </>
  )
}
GuideLocation.propTypes = {
  height: PropTypes.number
}

export default GuideLocation
